import { useCallback, useEffect, useRef, useState } from "react";
import { Copy, Info, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { API_URL, STORAGE } from "@/_config";
import { copyToClipboard } from "@/_config/functions";

interface FlaggedTerm {
  term: string;
  category: string;
  tone: string;
}

interface SpamCategory {
  key: string;
  label: string;
  emoji: string;
  tone: string;
  count: number;
  words: string[];
}

interface SpamAnalysis {
  overall: { label: string; tone: string };
  score: number;
  word_count: number;
  read_time_minutes: number;
  categories: SpamCategory[];
  flagged_terms: FlaggedTerm[];
  suggestions: string[];
}

interface SpamCheckModalProps {
  open: boolean;
  onClose: () => void;
  /** Latest editor content (HTML). The modal always analyses this on open. */
  body: string;
  subject?: string;
  /** Whether the AI Correction feature is available (plan-gated). */
  canUseAi: boolean;
}

const getToken = (): string => {
  const raw = localStorage.getItem(STORAGE);
  const stored = raw ? JSON.parse(raw) : null;
  return stored?.token ?? stored?.access_token ?? "";
};

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Canonical key for a term or matched span — mirrors the backend's termKey():
 *  lowercase, straight apostrophes, hyphen/whitespace runs collapsed to a space. */
const termKey = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[-\s ]+/g, " ")
    .trim();

/** Term → regex alternative — mirrors the backend's termToPattern():
 *  apostrophes match curly variants, spaces/hyphens are interchangeable and
 *  match across line breaks, word boundaries only at word-char edges. */
const termToPattern = (term: string): string => {
  let p = escapeRegExp(term)
    .replace(/'/g, "['’‘]")
    .replace(/[- ]+/g, "[-\\s ]+");
  if (/^[\p{L}\p{N}]/u.test(term)) p = "\\b" + p;
  if (/[\p{L}\p{N}]$/u.test(term)) p = p + "\\b";
  return p;
};

/** Convert an HTML email body into readable plain text, preserving paragraph breaks. */
const htmlToDisplayText = (html: string): string => {
  const withBreaks = html.replace(
    /<\s*\/?\s*(div|p|br|blockquote|table|tr|td|th|li|ul|ol|h[1-6]|section|article)\b[^>]*>/gi,
    "\n",
  );
  const div = document.createElement("div");
  div.innerHTML = withBreaks;
  const text = div.textContent ?? div.innerText ?? "";
  return text.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
};

/** Render text with flagged spam words wrapped in a colour-coded <mark>.
 *  Uses the same matching semantics as the backend analyser (longest-first,
 *  apostrophe/hyphen/line-break tolerant) so every counted hit is highlighted. */
const renderHighlighted = (text: string, flagged: FlaggedTerm[]): React.ReactNode => {
  if (!flagged.length || !text) return text;

  const toneByKey = new Map<string, string>();
  flagged.forEach((f) => {
    const key = termKey(f.term);
    if (!toneByKey.has(key)) toneByKey.set(key, f.tone);
  });

  // Longest-first so multi-word phrases win over their sub-words.
  const terms = flagged.map((f) => f.term).sort((a, b) => b.length - a.length);

  let pattern: RegExp;
  try {
    pattern = new RegExp(`(${terms.map(termToPattern).join("|")})`, "giu");
  } catch {
    return text;
  }

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    const matched = match[0];
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    const tone = toneByKey.get(termKey(matched)) ?? "pink";
    nodes.push(
      <mark key={`hl-${key++}`} className={`sc-hl sc-hl--${tone}`}>
        {matched}
      </mark>,
    );
    lastIndex = start + matched.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

export default function SpamCheckModal({ open, onClose, body, subject, canUseAi }: SpamCheckModalProps) {
  const [currentContent, setCurrentContent] = useState("");
  const [displayText, setDisplayText] = useState("");
  const [analysis, setAnalysis] = useState<SpamAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const runAnalyze = useCallback(
    async (content: string, background = false) => {
      if (background) setRefreshing(true);
      else setLoading(true);
      setError("");
      try {
        const res = await fetch(API_URL.SEQUENCES.SPAM_CHECK_ANALYZE, {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
          body: JSON.stringify({ body: content, subject: subject ?? "" }),
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.success) {
          throw new Error(json?.error?.message ?? "Failed to analyse content.");
        }
        setAnalysis(json.data as SpamAnalysis);
      } catch (err) {
        if (!background) setAnalysis(null);
        setError(err instanceof Error ? err.message : "Failed to analyse content.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [subject],
  );

  // Whenever the modal opens, re-seed from the latest editor content and analyse it.
  useEffect(() => {
    if (!open) return;
    const content = body ?? "";
    setCurrentContent(content);
    setDisplayText(htmlToDisplayText(content));
    setAnalysis(null);
    setError("");
    if (content.trim()) {
      void runAnalyze(content);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, body, runAnalyze]);

  if (!open) return null;

  // User edits the content directly in the modal: the edited text becomes the
  // canonical content (used by Copy / AI Correction) and the analysis
  // auto-refreshes after a short pause in typing.
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setDisplayText(text);
    setCurrentContent(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (text.trim()) void runAnalyze(text, true);
      else setAnalysis(null);
    }, 600);
  };

  const handleEditorScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (backdropRef.current) {
      backdropRef.current.scrollTop = e.currentTarget.scrollTop;
      backdropRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(currentContent);
    if (ok) toast.success("Content copied to clipboard");
    else toast.error("Failed to copy content");
  };

  const handleAiCorrect = async () => {
    setCorrecting(true);
    setError("");
    try {
      const res = await fetch(API_URL.SEQUENCES.SPAM_CHECK_AI_CORRECT, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          body: currentContent,
          subject: subject ?? "",
          flagged_terms: analysis?.flagged_terms ?? [],
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) {
        throw new Error(json?.error?.message ?? "AI Correction failed.");
      }
      const corrected: string = json.data?.corrected_body ?? "";
      if (!corrected.trim()) throw new Error("AI returned empty content.");
      setCurrentContent(corrected);
      setDisplayText(htmlToDisplayText(corrected));
      toast.success("Content rewritten to reduce spam risk");
      // Re-analyse the freshly corrected content so the score/highlights refresh.
      await runAnalyze(corrected);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "AI Correction failed.");
    } finally {
      setCorrecting(false);
    }
  };

  const overallTone = analysis?.overall?.tone ?? "good";

  return (
    <div className="sc-overlay" onClick={onClose}>
      <div className="sc-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sc-modal-header">
          <div className="sc-header-titles">
            <span className="sc-eyebrow">SPAM CHECKER</span>
            <h2 className="sc-title">Spam Checker</h2>
            <p className="sc-subtitle">Detect spam words — edit the content and the analysis updates automatically.</p>
          </div>
          <button className="sc-close-btn" onClick={onClose} type="button" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body: two panels */}
        <div className="sc-modal-body">
          <div className="sc-left-panel">
            <div className="sc-editor-wrap">
              <div className="sc-backdrop" ref={backdropRef} aria-hidden="true">
                {renderHighlighted(displayText, analysis?.flagged_terms ?? [])}
                {"\n"}
              </div>
              <textarea
                className="sc-editor"
                value={displayText}
                onChange={handleTextChange}
                onScroll={handleEditorScroll}
                placeholder="Write or paste your email content here to analyse it…"
                spellCheck={false}
              />
            </div>
          </div>

          <div className="sc-right-panel">
            {loading ? (
              <div className="sc-analyzing">
                <span className="sc-spinner" /> Analysing…
              </div>
            ) : error ? (
              <p className="sc-error">{error}</p>
            ) : analysis ? (
              <>
                {refreshing && (
                  <div className="sc-refreshing">
                    <span className="sc-spinner" /> Updating…
                  </div>
                )}
                <div className="sc-stats">
                  <div className="sc-stat-row">
                    <span className="sc-stat-label">Overall score:</span>
                    <span className={`sc-stat-value sc-tone--${overallTone}`}>{analysis.overall.label}</span>
                  </div>
                  <div className="sc-stat-row">
                    <span className="sc-stat-label">Words:</span>
                    <span className="sc-stat-value">{analysis.word_count}</span>
                  </div>
                  <div className="sc-stat-row">
                    <span className="sc-stat-label">Read time:</span>
                    <span className="sc-stat-value">
                      {analysis.read_time_minutes} min
                    </span>
                  </div>
                </div>

                {analysis.categories.length > 0 ? (
                  <div className="sc-categories">
                    {analysis.categories.map((cat) => (
                      <div key={cat.key} className={`sc-cat-card sc-cat--${cat.tone}`}>
                        <span className="sc-cat-emoji">{cat.emoji}</span>
                        <span className="sc-cat-label">{cat.label}</span>
                        <span className="sc-cat-count">({cat.count})</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="sc-clean">No spam trigger words detected. 🎉</p>
                )}

                {analysis.suggestions.length > 0 && (
                  <div className="sc-suggestions">
                    <span className="sc-suggestions-title">Suggestions</span>
                    <ul>
                      {analysis.suggestions.map((s, i) => (
                        <li key={`sug-${i}`}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <p className="sc-empty">Start typing on the left to analyse your email.</p>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="sc-modal-footer">
          <div className="sc-footer-note">
            <Info size={15} className="sc-footer-note-icon" />
            <span>
              <strong>Note:</strong> Changes here are not saved automatically — click{" "}
              <strong>Copy</strong> and paste the corrected content into your Sequence Email Body.
            </span>
          </div>
          <button
            className="sc-btn sc-btn-ghost"
            type="button"
            onClick={handleCopy}
            disabled={!currentContent.trim()}
          >
            <Copy size={15} /> Copy
          </button>
          {canUseAi && (
            <button
              className="sc-btn sc-btn-primary"
              type="button"
              onClick={handleAiCorrect}
              disabled={correcting || loading || !currentContent.trim()}
            >
              {correcting ? (
                <>
                  <span className="sc-spinner sc-spinner--light" /> Correcting…
                </>
              ) : (
                <>
                  <Sparkles size={15} /> AI Correction
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
